import { FormEvent, useCallback, useMemo, useState } from 'react';
import debounce from 'lodash/debounce';
import { useEffectOnce } from 'react-use';
import { BusinessGeoDto } from '../../../data-layer/business/types';
import { loadGeoSuggestsAsync } from '../../../data-layer/geo/actions';
import { selectGeo } from '../../../data-layer/geo/selectors';
import { useDispatch } from '../../../data-layer/useDispatch';
import { useSelector } from '../../../data-layer/useSelector';
import { useTranslation } from '../../../utils/useTranslation';

const DEFAULT_GEO_NAME = 'Россия';

const DEFAULT_GEO_REGION_ID = '225';

const DEFAULT_GEO = { [DEFAULT_GEO_REGION_ID]: DEFAULT_GEO_NAME };

const TIMEOUT = 200;

function getRegionsSettings(regions?: BusinessGeoDto[]) {
    if (!regions?.length) {
        return DEFAULT_GEO;
    }

    return regions.reduce<Record<string, string>>(
        (res, region) => ({
            ...res,
            [String(region.region_id)]: region.name,
        }),
        {},
    );
}

function areRegionsEqual(
    prevRegions: Record<string, string>,
    nextRegions: Record<string, string>,
): boolean {
    const prevRegionsIds = Object.keys(prevRegions);
    const nextRegionsIds = Object.keys(nextRegions);

    if (prevRegionsIds.length !== nextRegionsIds.length) {
        return false;
    }

    return prevRegionsIds.every((key) => prevRegions[key] === nextRegions[key]);
}

export type UseBusinessGeoOptions = {
    /**
     * При выборе региона, отличного от гео по умолчанию (Россия), убрать гео по умолчанию из списка.
     * При удалении последнего выбранного региона снова выставить гео по умолчанию.
     */
    syncDefaultGeo?: boolean;
};

export const useBusinessGeo = (
    regions?: BusinessGeoDto[],
    { syncDefaultGeo = false }: UseBusinessGeoOptions = {},
) => {
    const t = useTranslation();
    const defaultRegionError = t(
        'Вы уже выбрали регионы. Удалите текущие регионы и укажите «Россия», если товары и услуги доступны по всей стране.',
    );
    const dispatch = useDispatch();
    const [geoChanged, setGeoChanged] = useState(false);
    const geo = useSelector((state) => selectGeo(state));
    const [regionQuery, setRegionQuery] = useState('');
    const [errorMessage, setErrorMessage] = useState<string | undefined>();

    const onLoadGeoSuggests = useMemo(
        () =>
            debounce((query?: string) => {
                dispatch(loadGeoSuggestsAsync(query));
            }, TIMEOUT),
        [dispatch],
    );

    const initialSelectedRegions = getRegionsSettings(regions);
    const [selectedRegions, setSelectedRegions] =
        useState<Record<string, string>>(initialSelectedRegions);

    const onSelectRegion = useCallback(
        (value: Record<string, string>) => {
            const newSelectedRegions = { ...selectedRegions, ...value };
            const hasNotDefaultRegion = Object.keys(newSelectedRegions).some(
                (id) => id !== DEFAULT_GEO_REGION_ID,
            );

            if (syncDefaultGeo && DEFAULT_GEO_REGION_ID in value && hasNotDefaultRegion) {
                setErrorMessage(defaultRegionError);

                return;
            }

            if (
                syncDefaultGeo &&
                hasNotDefaultRegion &&
                DEFAULT_GEO_REGION_ID in newSelectedRegions
            ) {
                delete newSelectedRegions[DEFAULT_GEO_REGION_ID];
            }

            setErrorMessage(undefined);
            setSelectedRegions(newSelectedRegions);
            setRegionQuery('');

            const isChanged = !areRegionsEqual(initialSelectedRegions, newSelectedRegions);

            setGeoChanged(isChanged);
        },
        [selectedRegions, syncDefaultGeo, defaultRegionError, initialSelectedRegions],
    );

    const onRemoveRegion = useCallback(
        (key: string) => {
            const newSelectedRegions = { ...selectedRegions };

            delete newSelectedRegions[key];

            const nextSelectedRegions =
                syncDefaultGeo && Object.keys(newSelectedRegions).length === 0
                    ? { ...DEFAULT_GEO }
                    : newSelectedRegions;

            setSelectedRegions(nextSelectedRegions);

            setErrorMessage(undefined);

            const isChanged = !areRegionsEqual(initialSelectedRegions, nextSelectedRegions);

            setGeoChanged(isChanged);
        },
        [selectedRegions, syncDefaultGeo, initialSelectedRegions],
    );

    const onInputRegion = useCallback(
        (event: FormEvent<HTMLInputElement>) => {
            const value = event.currentTarget.value;

            setRegionQuery(value);
            onLoadGeoSuggests(value);
            setErrorMessage(undefined);
        },
        [onLoadGeoSuggests],
    );

    const updateSelectedRegions = useCallback((newRegions?: BusinessGeoDto[]) => {
        setSelectedRegions(getRegionsSettings(newRegions));
    }, []);

    useEffectOnce(() => {
        onLoadGeoSuggests(DEFAULT_GEO_NAME);
    });

    return {
        selectedRegions,
        geoSuggests: geo.suggests,
        regionQuery,
        geoChanged,
        errorMessage,
        onSelectRegion,
        onRemoveRegion,
        onInputRegion,
        updateSelectedRegions,
    };
};
